import { useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../api';
import { useAuth } from '../context/AuthContext';
import DropZone from '../components/DropZone';
import { styles, colors, fmtDate, useIsMobile } from '../components/UI';

// RM Quotes (George 2026-10-28): drop RentalMan sales quote PDFs or the SSS
// Excel template, review, and download an SSS-branded PDF named
// "<Customer> - <quote #>.pdf". Prices are LOCKED to the file (server-side,
// utils/rmSalesQuote applyEdits): to change a price, fix it in RentalMan or
// the workbook and drop it again.

const SOURCE_LABEL = { pdf: 'RM PDF', excel_template: 'Excel template', excel_ai: 'Excel' };
const ACCEPT = '.pdf,.xlsx,.xlsm';

const lockedCell = { ...styles.td, color: colors.textMuted, background: colors.graySoft, whiteSpace: 'nowrap', textAlign: 'right' };
// Quote money shows to the cent (UI.fmtMoney rounds to whole dollars).
const cents = (v) => (v == null || v === '' ? '' : Number(v).toLocaleString('en-US', { style: 'currency', currency: 'USD' }));
// A rental row's price is its rate ladder, not a unit price.
const rateText = (l) => [['Min', l.rate_min], ['Day', l.rate_day], ['Wk', l.rate_week], ['4 Wk', l.rate_4week]]
  .filter(([, v]) => v != null && v !== '').map(([k, v]) => `${k} ${cents(v)}`).join(' / ');
const unitMoney = (n) => (n == null || n === '' ? '' : Number(n).toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 3 }));

export default function RmQuotes() {
  const { user } = useAuth();
  const isMobile = useIsMobile();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState('');
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState(null);
  const fileRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try { setRows(await api.listRmQuotes()); } catch (e) { setError(e.message); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  // A drop that misses the zone must not make the browser open the file.
  useEffect(() => {
    const prevent = (e) => { e.preventDefault(); };
    window.addEventListener('dragover', prevent);
    window.addEventListener('drop', prevent);
    return () => { window.removeEventListener('dragover', prevent); window.removeEventListener('drop', prevent); };
  }, []);

  const uploadFiles = async (list) => {
    const files = Array.from(list || []).filter(Boolean);
    if (!files.length) return;
    if (files.length > 10) { setError('Drop up to 10 files at a time.'); return; }
    setError(''); setResults([]);
    setBusy(`Reading ${files.length} file${files.length === 1 ? '' : 's'}... (a PDF takes 20-40 seconds)`);
    try {
      const { results: rs } = await api.uploadRmQuotes(files);
      setResults(rs);
      await load();
      const firstOk = rs.find(r => r.ok);
      if (firstOk) setSelected(firstOk.quote);
    } catch (e) { setError(e.message); } finally {
      setBusy('');
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const download = async (q) => {
    setError('');
    try { await api.downloadRmQuoteFile(`${q.id}/pdf`, q.file_name); } catch (e) { setError(e.message); }
  };

  return (
    <div style={styles.container}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <h1 style={{ ...styles.h1, marginBottom: 4 }}>RM Quotes</h1>
          <div style={{ color: colors.textMuted, fontSize: 13, marginBottom: 16 }}>
            Drop a RentalMan sales quote PDF or a filled SSS Excel template. You get an SSS-branded quote PDF named by customer and quote number.
          </div>
        </div>
        <button style={styles.btnSecondary} onClick={() => api.downloadRmQuoteFile('template', 'SSS Sales Quote Template.xlsx').catch(e => setError(e.message))}>
          ⬇ Excel template
        </button>
      </div>

      {error && <div style={styles.errorBox}>{error}</div>}

      <DropZone block onFiles={uploadFiles} disabled={!!busy} style={{ marginBottom: 16 }} title="Drop PDF or Excel quotes here">
        <div
          onClick={() => !busy && fileRef.current && fileRef.current.click()}
          style={{
            border: `2px dashed ${colors.border}`, borderRadius: 8, padding: isMobile ? 20 : 32, textAlign: 'center',
            background: colors.card, cursor: busy ? 'wait' : 'pointer',
          }}
        >
          <div style={{ fontSize: 28 }}>📄</div>
          <div style={{ fontWeight: 600, color: colors.charcoal, marginTop: 6 }}>
            {busy || 'Drag and drop quote files here, or click to browse'}
          </div>
          <div style={{ color: colors.textMuted, fontSize: 12, marginTop: 4 }}>PDF (RentalMan sales quote) or Excel .xlsx, up to 10 at a time</div>
          <input ref={fileRef} type="file" accept={ACCEPT} multiple style={{ display: 'none' }} onChange={e => uploadFiles(e.target.files)} />
        </div>
      </DropZone>

      {results.some(r => !r.ok) && (
        <div style={styles.errorBox}>
          {results.filter(r => !r.ok).map((r, i) => <div key={i}><b>{r.file}</b>: {r.error}</div>)}
        </div>
      )}

      {selected && (
        <QuoteEditor
          key={selected.id}
          quote={selected}
          userId={user.id}
          onChange={(q) => { setSelected(q); setRows(rs => rs.map(r => (r.id === q.id ? q : r))); }}
          onClose={() => setSelected(null)}
          onDeleted={(id) => { setSelected(null); setRows(rs => rs.filter(r => r.id !== id)); }}
          onDownload={download}
        />
      )}

      <div style={styles.card}>
        <h2 style={styles.h2}>Quotes</h2>
        {loading ? <div style={{ color: colors.textMuted }}>Loading...</div> : !rows.length ? (
          <div style={{ color: colors.textMuted }}>No quotes yet. Drop one above.</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Quote #</th>
                  <th style={styles.th}>Customer</th>
                  <th style={{ ...styles.th, textAlign: 'right' }}>Total</th>
                  <th style={styles.th}>From</th>
                  <th style={styles.th}>Opportunity</th>
                  <th style={styles.th}>Status</th>
                  <th style={styles.th}>Added</th>
                  <th style={styles.th}></th>
                </tr>
              </thead>
              <tbody>
                {rows.map(q => (
                  <tr key={q.id} style={selected && selected.id === q.id ? { background: '#FFF7ED' } : undefined}>
                    <td style={{ ...styles.td, fontWeight: 600, whiteSpace: 'nowrap' }}>{q.quote_no || '-'}</td>
                    <td style={styles.td}>{q.customer_name || '-'}</td>
                    <td style={{ ...styles.td, textAlign: 'right', whiteSpace: 'nowrap' }}>{cents(q.totals.total)}</td>
                    <td style={styles.td}>{SOURCE_LABEL[q.source] || q.source}</td>
                    <td style={styles.td}>
                      {q.project_label || <span style={{ color: colors.textMuted }}>-</span>}{q.filed_at ? ' ✓ filed' : ''}
                      {q.last_emailed_at && <div style={{ color: colors.textMuted, fontSize: 12 }}>✉ {fmtDate(q.last_emailed_at)} to {q.last_emailed_to}</div>}
                    </td>
                    <td style={styles.td}>
                      {q.errors.length
                        ? <span style={{ ...styles.pill, background: colors.redSoft, color: colors.red }}>{q.errors.length} issue{q.errors.length === 1 ? '' : 's'}</span>
                        : <span style={{ ...styles.pill, background: colors.greenSoft, color: '#047857' }}>Ready</span>}
                    </td>
                    <td style={{ ...styles.td, whiteSpace: 'nowrap' }}>{fmtDate(q.created_at)}<div style={{ color: colors.textMuted, fontSize: 12 }}>{q.created_by_name}</div></td>
                    <td style={{ ...styles.td, whiteSpace: 'nowrap' }}>
                      <button style={{ ...styles.btnSecondary, padding: '5px 10px', marginRight: 6 }} onClick={() => setSelected(q)}>Review</button>
                      <button style={{ ...styles.btn, padding: '5px 10px' }} disabled={!!q.errors.length} title={q.errors.length ? 'Fix the issues first' : q.file_name} onClick={() => download(q)}>PDF</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// Review card. Editable: parties, contact, dates, notes, line wording.
// Locked (grey): qty, unit, prices, amounts, totals.
function QuoteEditor({ quote, userId, onChange, onClose, onDeleted, onDownload }) {
  const [d, setD] = useState(() => JSON.parse(JSON.stringify(quote.data)));
  const [projectId, setProjectId] = useState(quote.project_id || '');
  const [projects, setProjects] = useState(null);
  const [projFilter, setProjFilter] = useState('');
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState('');
  const [err, setErr] = useState('');
  const { user } = useAuth();
  const canDelete = quote.created_by === userId || user.role === 'admin' || user.role === 'leadership';

  useEffect(() => { api.listProjects({}).then(setProjects).catch(() => setProjects([])); }, []);
  useEffect(() => {
    if (!dirty) return undefined;
    const warn = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  const set = (path, v) => {
    setD(prev => {
      const next = JSON.parse(JSON.stringify(prev));
      const keys = path.split('.');
      let o = next;
      keys.slice(0, -1).forEach(k => { if (o[k] == null) o[k] = {}; o = o[k]; });
      o[keys[keys.length - 1]] = v;
      return next;
    });
    setDirty(true);
  };
  const setLine = (i, k, v) => {
    setD(prev => ({ ...prev, lines: prev.lines.map((l, n) => (n === i ? { ...l, [k]: v } : l)) }));
    setDirty(true);
  };
  const linesText = (arr) => (arr || []).join('\n');
  const toLines = (s) => String(s || '').split('\n').map(x => x.trim()).filter(Boolean);

  const projOptions = useMemo(() => {
    const f = projFilter.trim().toLowerCase();
    const list = (projects || []).filter(p => !f || `${p.site} ${p.job_number || ''}`.toLowerCase().includes(f) || p.id === Number(projectId));
    return list.slice(0, 200);
  }, [projects, projFilter, projectId]);

  const save = async () => {
    setSaving('Saving...'); setErr('');
    try {
      const q = await api.updateRmQuote(quote.id, { data: d, project_id: projectId === '' ? null : Number(projectId) });
      setD(JSON.parse(JSON.stringify(q.data)));
      setDirty(false);
      onChange(q);
      return q;
    } catch (e) { setErr(e.message); return null; } finally { setSaving(''); }
  };
  const saveThen = async (fn) => {
    const q = dirty || (Number(projectId) || null) !== (quote.project_id || null) ? await save() : quote;
    if (q) await fn(q);
  };
  const fileIt = () => saveThen(async (q) => {
    setSaving('Filing...'); setErr('');
    try { onChange(await api.fileRmQuote(q.id)); } catch (e) { setErr(e.message); } finally { setSaving(''); }
  });
  const del = async () => {
    if (!window.confirm(`Delete quote ${quote.quote_no}? A copy already filed on an opportunity stays there.`)) return;
    try { await api.deleteRmQuote(quote.id); onDeleted(quote.id); } catch (e) { setErr(e.message); }
  };

  const field = (label, path, opts = {}) => {
    const v = path.split('.').reduce((o, k) => (o == null ? '' : o[k]), d);
    return (
      <div>
        <label style={styles.label}>{label}</label>
        {opts.multi
          ? <textarea style={{ ...styles.textarea, minHeight: 54 }} value={linesText(v)} onChange={e => set(path, e.target.value.split('\n'))} onBlur={e => set(path, toLines(e.target.value))} />
          : <input style={styles.input} type={opts.type || 'text'} value={v || ''} onChange={e => set(path, e.target.value)} />}
      </div>
    );
  };
  const errs = quote.errors || [];
  const badLines = new Set(errs.filter(e => e.line).map(e => e.line));

  return (
    <div style={{ ...styles.card, borderColor: colors.orange }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
        <h2 style={{ ...styles.h2, margin: 0 }}>
          Quote {quote.quote_no}{dirty && <span style={{ color: colors.amber, fontSize: 12, marginLeft: 8 }}>● unsaved</span>}
        </h2>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button style={styles.btnSecondary} onClick={save} disabled={!!saving}>Save</button>
          <button style={styles.btn} disabled={!!saving || !!errs.length} title={errs.length ? 'Fix the issues first' : quote.file_name}
            onClick={() => saveThen(onDownload)}>⬇ Download PDF</button>
          <button style={styles.btnSecondary} disabled={!!saving || !!errs.length || !projectId} title={projectId ? 'Attach to the opportunity' : 'Pick an opportunity first'}
            onClick={fileIt}>{quote.filed_at ? 'Refile on opportunity' : 'File on opportunity'}</button>
          <button style={styles.btnSecondary} onClick={() => api.downloadRmQuoteFile(`${quote.id}/source`, quote.source_filename).catch(e => setErr(e.message))}>Original</button>
          {canDelete && <button style={styles.btnDanger} onClick={del}>Delete</button>}
          <button style={styles.btnSecondary} onClick={() => { if (!dirty || window.confirm('Discard unsaved changes?')) onClose(); }}>Close</button>
        </div>
      </div>
      <div style={{ color: colors.textMuted, fontSize: 12, marginBottom: 12 }}>
        Saves as <b>{quote.file_name}</b>. {saving}
        {quote.filed_at && <> Filed on the opportunity {fmtDate(quote.filed_at)}.</>}
      </div>

      {err && <div style={styles.errorBox}>{err}</div>}
      {!!errs.length && (
        <div style={styles.errorBox}>
          <b>This quote cannot be issued until these are fixed.</b> Prices come from the file: correct the quote in RentalMan or the workbook and drop it again.
          <ul style={{ margin: '6px 0 0 18px', padding: 0 }}>{errs.map((e, i) => <li key={i}>{e.msg}</li>)}</ul>
        </div>
      )}

      <div style={styles.formGrid}>
        {field('Customer', 'customer.name')}
        {field('Customer #', 'customer.number')}
        {field('Customer address (one line each)', 'customer.lines', { multi: true })}
        {field('Ship to (blank = same as customer)', 'ship_to.name')}
        {field('Ship-to address', 'ship_to.lines', { multi: true })}
        {field('Site phone', 'ship_to.phone')}
      </div>
      <div style={styles.formGrid}>
        {field('Ordered by', 'ordered_by')}
        {field('Customer PO', 'po_number')}
        {field('Quote date', 'quote_date', { type: 'date' })}
        {field('Valid through', 'valid_until', { type: 'date' })}
        {field('Rental date out', 'date_out', { type: 'date' })}
        {field('Rental est. return', 'est_return', { type: 'date' })}
        {field('Payment terms', 'payment_terms')}
        {field('Sales rep', 'sales_rep')}
        {field('Rep phone', 'rep_phone')}
        {field('Rep email', 'rep_email')}
        {field('Prepared by', 'written_by')}
        {field('Branch', 'branch')}
      </div>
      <div style={styles.formGrid}>
        <div>
          <label style={styles.label}>Opportunity (optional, to file the PDF)</label>
          <input style={{ ...styles.input, marginBottom: 6 }} placeholder="Filter by site or job #" value={projFilter} onChange={e => setProjFilter(e.target.value)} />
          <select style={styles.input} value={projectId} onChange={e => { setProjectId(e.target.value); setDirty(true); }}>
            <option value="">{projects ? '- none -' : 'Loading...'}</option>
            {projOptions.map(p => <option key={p.id} value={p.id}>{[p.job_number, p.site, p.status].filter(Boolean).join(' - ')}</option>)}
          </select>
        </div>
        {field('Notes (one per line)', 'notes', { multi: true })}
      </div>

      <EmailPanel quote={quote} disabled={!!errs.length} saveFirst={saveThen} onSent={() => api.getRmQuote(quote.id).then(onChange).catch(() => {})} />

      <div style={{ overflowX: 'auto' }}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>#</th>
              <th style={styles.th}>Part #</th>
              <th style={{ ...styles.th, minWidth: 260 }}>Description</th>
              <th style={{ ...styles.th, minWidth: 180 }}>Details (one per line)</th>
              <th style={{ ...styles.th, textAlign: 'right' }}>🔒 Qty</th>
              <th style={styles.th}>Unit</th>
              <th style={{ ...styles.th, textAlign: 'right' }}>🔒 Unit price / rates</th>
              <th style={{ ...styles.th, textAlign: 'right' }}>🔒 Extended</th>
            </tr>
          </thead>
          <tbody>
            {(d.lines || []).map((l, i) => (
              <tr key={i} style={badLines.has(i + 1) ? { background: colors.redSoft } : undefined}>
                <td style={styles.td}>{i + 1}{l.kind === 'rental' && <div style={{ fontSize: 10, color: colors.blue, fontWeight: 700 }}>RENTAL</div>}</td>
                <td style={{ ...styles.td, whiteSpace: 'nowrap' }}>{l.part_no}</td>
                <td style={styles.td}>
                  <input style={styles.input} value={l.description || ''} onChange={e => setLine(i, 'description', e.target.value)} />
                  {l.rm_description && l.rm_description !== l.description && (
                    <div style={{ color: colors.textMuted, fontSize: 11, marginTop: 3 }}>RM: {l.rm_description}</div>
                  )}
                </td>
                <td style={styles.td}>
                  <textarea style={{ ...styles.textarea, minHeight: 36 }} value={linesText(l.details)}
                    onChange={e => setLine(i, 'details', e.target.value.split('\n'))} onBlur={e => setLine(i, 'details', toLines(e.target.value))} />
                </td>
                <td style={lockedCell}>{l.qty}</td>
                <td style={{ ...lockedCell, textAlign: 'left' }}>{l.unit || 'EA'}</td>
                <td style={lockedCell}>{l.kind === 'rental' ? rateText(l) : unitMoney(l.unit_price)}</td>
                <td style={lockedCell}>{!Number(l.amount) && (l.amount_note || (l.unit_price == null && !rateText(l))) ? (l.amount_note || 'N/C') : cents(l.amount)}</td>
              </tr>
            ))}
            {!!quote.totals.tax && (
              <>
                <tr>
                  <td colSpan={7} style={{ ...styles.td, textAlign: 'right' }}>Subtotal</td>
                  <td style={lockedCell}>{cents(quote.totals.subtotal)}</td>
                </tr>
                <tr>
                  <td colSpan={7} style={{ ...styles.td, textAlign: 'right' }}>Sales tax</td>
                  <td style={lockedCell}>{cents(quote.totals.tax)}</td>
                </tr>
              </>
            )}
            <tr>
              <td colSpan={7} style={{ ...styles.td, textAlign: 'right', fontWeight: 700 }}>Total</td>
              <td style={{ ...lockedCell, fontWeight: 700, color: colors.charcoal }}>{cents(quote.totals.total)}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

// Email the quote to the customer. The rep types the address(es); the server
// always CCs the signed-in user and sets them as Reply-To. History lists every
// attempt (sent or failed); Resend sends the CURRENT PDF to the same people.
function EmailPanel({ quote, disabled, saveFirst, onSent }) {
  const [to, setTo] = useState('');
  const [cc, setCc] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [history, setHistory] = useState([]);
  const loadHistory = () => api.listRmQuoteEmails(quote.id).then(setHistory).catch(() => {});
  useEffect(() => { loadHistory(); }, [quote.id]);

  const done = async (fn, okText) => {
    setBusy(true); setMsg(null);
    try { await fn(); setMsg({ ok: true, text: okText }); onSent(); } catch (e) { setMsg({ ok: false, text: e.message }); } finally {
      setBusy(false); loadHistory();
    }
  };
  const send = () => saveFirst(async (q) => done(async () => {
    const r = await api.emailRmQuote(q.id, { to, cc, message });
    setTo(''); setCc(''); setMessage('');
    return r;
  }, "Sent. You are CC'd."));
  const resend = (e) => {
    if (!window.confirm(`Resend quote ${quote.quote_no} to ${e.to_addrs}${e.cc_addrs ? ` (cc ${e.cc_addrs})` : ''} with the current PDF?`)) return;
    saveFirst(async (q) => done(() => api.resendRmQuoteEmail(q.id, e.id), "Resent. You are CC'd."));
  };

  return (
    <div style={{ border: `1px solid ${colors.border}`, borderRadius: 8, padding: 14, marginBottom: 16, background: colors.graySoft }}>
      <div style={{ fontWeight: 700, color: colors.charcoal, marginBottom: 8 }}>✉ Email quote to customer</div>
      <div style={styles.formGrid}>
        <div>
          <label style={styles.label}>To (customer email, separate several with commas)</label>
          <input style={styles.input} value={to} onChange={e => setTo(e.target.value)} placeholder="buyer@customer.com" />
        </div>
        <div>
          <label style={styles.label}>CC (optional; you are always CC'd)</label>
          <input style={styles.input} value={cc} onChange={e => setCc(e.target.value)} />
        </div>
      </div>
      <label style={styles.label}>Message (optional, goes above the standard note)</label>
      <textarea style={{ ...styles.textarea, minHeight: 60 }} value={message} onChange={e => setMessage(e.target.value)} />
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 8, flexWrap: 'wrap' }}>
        <button style={styles.btn} disabled={busy || disabled || !to.trim()} title={disabled ? 'Fix the issues first' : ''} onClick={send}>
          {busy ? 'Sending...' : 'Send email'}
        </button>
        <span style={{ fontSize: 12, color: colors.textMuted }}>Attaches {quote.file_name}. Replies come to you.</span>
        {msg && <span style={{ fontSize: 13, fontWeight: 600, color: msg.ok ? '#047857' : colors.red }}>{msg.text}</span>}
      </div>

      {!!history.length && (
        <div style={{ marginTop: 12, overflowX: 'auto' }}>
          <div style={{ fontWeight: 600, fontSize: 13, color: colors.charcoal, marginBottom: 4 }}>Email history</div>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Sent</th>
                <th style={styles.th}>To</th>
                <th style={styles.th}>CC</th>
                <th style={{ ...styles.th, textAlign: 'right' }}>Total</th>
                <th style={styles.th}>Status</th>
                <th style={styles.th}>By</th>
                <th style={styles.th}></th>
              </tr>
            </thead>
            <tbody>
              {history.map(e => (
                <tr key={e.id}>
                  <td style={{ ...styles.td, whiteSpace: 'nowrap' }}>{new Date(e.sent_at).toLocaleString('en-US', { month: 'numeric', day: 'numeric', year: '2-digit', hour: 'numeric', minute: '2-digit' })}{e.resend_of ? <div style={{ fontSize: 11, color: colors.textMuted }}>resend</div> : null}</td>
                  <td style={styles.td}>{e.to_addrs}</td>
                  <td style={{ ...styles.td, color: colors.textMuted }}>{e.cc_addrs || '-'}</td>
                  <td style={{ ...styles.td, textAlign: 'right' }}>{cents(e.total)}</td>
                  <td style={styles.td}>
                    {e.status === 'sent'
                      ? <span style={{ ...styles.pill, background: colors.greenSoft, color: '#047857' }}>Sent</span>
                      : <span style={{ ...styles.pill, background: colors.redSoft, color: colors.red }} title={e.error || ''}>Failed</span>}
                  </td>
                  <td style={styles.td}>{e.sent_by_name || '-'}</td>
                  <td style={styles.td}>
                    <button style={{ ...styles.btnSecondary, padding: '4px 10px' }} disabled={busy || disabled} onClick={() => resend(e)}>Resend</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
